import React from "react";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const worldHistoryUnit2Notes: UnitNotes = {
  unitId: "unit-2",
  unitName: "Unit 2: Networks of Exchange (c. 1200 – c. 1450)",
  examWeight: "8–10%",
  focus: "How the movement of people, goods, and ideas connected the world and altered societies.",
  sections: [
    {
      id: "topic-2-1",
      title: "Topic 2.1: The Silk Roads",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛣️ Causes of Growth in Silk Road Trade</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Trade along the Silk Roads (connecting China, Central Asia, the Middle East, and Europe) had existed for centuries but experienced a massive revival and expansion during this period due to several key factors.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">💎 Demand for Luxury Goods</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Europe and Africa:</strong> Desired Chinese silk, porcelain, and tea; Indian cotton and spices. <strong>China:</strong> Desired gold, silver, and horses from Central Asia. The Silk Road was primarily for <strong>luxury goods</strong> because commodities (grain, timber) were too heavy to transport overland profitably.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🚀 Innovations in Transportation & Commerce</h4>
                <div className="space-y-2 mt-2 text-sm text-slate-700">
                  <p><strong>Caravanserai:</strong> Roadside inns spaced about 100 miles apart (the distance a camel could walk before needing water). Travelers could rest, trade, and swap animals.</p>
                  <p><strong>Camel Saddles:</strong> Improvements allowed camels to carry heavier loads, increasing profitability.</p>
                  <p><strong>Flying Cash (Paper Money):</strong> Developed by the Song Dynasty; merchants deposited coins and received paper receipts, eliminating the danger of carrying heavy metal coins.</p>
                  <p><strong>Banking Houses:</strong> Precursors to modern banks; established in Europe and the Islamic world.</p>
                  <p><strong>Bills of Exchange:</strong> A written order for payment of a specific amount of money (essentially a check), allowing merchants to travel without large amounts of cash.</p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🏛️ Imperial Stability (Pax Mongolica)</h4>
                <p className="text-sm text-slate-700 mt-1">The rise of the <strong>Mongol Empire</strong> unified almost the entire Silk Road under one legal and political authority. The Mongols punished bandits severely, making the roads safe for travelers (e.g., Marco Polo and Ibn Battuta).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌆 Effects of Silk Road Trade</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Trade wealth led to the growth of major trading cities at strategic locations.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-amber-100 to-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">🏙️ Kashgar</h4>
                <p className="text-sm text-slate-700 mt-1">Located in Western China where the northern and southern Silk Road routes crossed. Became a center of Islamic scholarship and trade.</p>
              </div>
              <div className="bg-gradient-to-br from-orange-100 to-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🕌 Samarkand</h4>
                <p className="text-sm text-slate-700 mt-1">A stopping point between China and the Mediterranean in Uzbekistan. Known for religious diversity (Christianity, Buddhism, Zoroastrianism, and Islam) and magnificent Islamic architecture.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-2",
      title: "Topic 2.2: The Mongol Empire and the Making of the Modern World",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🐴 Rise of the Mongols</h3>
            <div className="space-y-3">
              <p className="text-slate-700 leading-relaxed">
                Pastoral nomads from the steppes of Central Asia; skilled horse riders and archers.
              </p>
              <KeyTerm 
                term="Genghis Khan (Temujin)" 
                definition="Unified the Mongol tribes in 1206. Used military tactics including the short bow (could shoot while riding), unit structure based on 10s (efficient command), feigned retreat (pretending to run away to lure enemies into a trap), and psychological warfare (if a city surrendered, they were spared; if they resisted, everyone was killed)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👑 Governance and the Khanates</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              After Genghis Khan's death, the empire was split into four Khanates:
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🏯 Yuan Dynasty (China)</h4>
                <p className="text-sm text-slate-700 mt-1">Ruled by <strong>Kublai Khan</strong>. He dismantled the Confucian exam system and placed Mongols in charge, but respected Chinese culture enough to adopt the dynastic name "Yuan."</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border-l-4 border-yellow-400">
                <h4 className="font-bold text-yellow-900">🇷🇺 The Golden Horde (Russia)</h4>
                <p className="text-sm text-slate-700 mt-1">Mongols did not occupy Russia (too many forests, not enough grazing land). Instead, they forced Russian princes to pay <strong>tribute</strong>. This isolation cut Russia off from the European Renaissance but led to the rise of Moscow as a tax-collecting center.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🕌 Il-Khanate (Middle East/Persia)</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Hulegu Khan</strong> conquered the Abbasid Caliphate (sacked Baghdad in 1258). Mongols here eventually converted to <strong>Islam</strong> and assimilated into Persian culture.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">🛣️ Chagatai Khanate (Central Asia)</h4>
                <p className="text-sm text-slate-700 mt-1">Controlled the Silk Road trade hubs.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">☮️ The Pax Mongolica (Mongol Peace)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">💰 Economic Impact</h4>
                <p className="text-sm text-slate-700 mt-1">The Mongols revitalized trade by building bridges, protecting roads, and punishing bandits.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🌍 Cultural Exchange</h4>
                <p className="text-sm text-slate-700 mt-1">They forced skilled artisans, scribes, and doctors to move across the empire, diffusing knowledge. <strong>Uyghur Script</strong> was adopted to unify the empire's record-keeping. Medical and mathematical knowledge transferred from the Islamic world to China and vice versa.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📉 Decline of the Mongols</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">⚔️ Political Fragmentation</h4>
                <p className="text-sm text-slate-700 mt-1">Succession disputes among Genghis's grandsons weakened the empire.</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🏯 In China (Yuan)</h4>
                <p className="text-sm text-slate-700 mt-1">The White Lotus Society rebellions (Red Turban Revolt) overthrew the Mongols, leading to the <strong>Ming Dynasty</strong> (1368).</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🦠 The Black Death</h4>
                <p className="text-sm text-slate-700 mt-1">The Mongols inadvertently spread the <strong>Bubonic Plague (Black Death)</strong> via trade routes, which decimated populations and economies, weakening their rule.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-3",
      title: "Topic 2.3: Exchange in the Indian Ocean",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌊 Causes of Growth</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Unlike the Silk Road (luxury goods), the Indian Ocean trade shipped <strong>bulk goods</strong> (timber, grain, ivory) because ships could carry much more than camels.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">💨 Environmental Knowledge</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Monsoon Winds:</strong> The key to Indian Ocean trade. Winds blew Northeast in winter and Southwest in summer. Merchants had to time their voyages and wait in port cities for winds to shift, creating cosmopolitan port communities.</p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🚢 Technological Innovations</h4>
                <div className="space-y-2 mt-2 text-sm text-slate-700">
                  <p><strong>Lateen Sail:</strong> Triangular sail allowed ships to tack against the wind.</p>
                  <p><strong>Sternpost Rudder:</strong> Gave ships more precision in steering (Chinese invention).</p>
                  <p><strong>Astrolabe:</strong> Allowed sailors to calculate latitude (Islamic improvement on Greek tech).</p>
                  <p><strong>Dhows (Arab/Indian) & Junks (Chinese):</strong> Large ship designs.</p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">👥 Commercial Expansion</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Diasporic Communities:</strong> Merchants settled in foreign ports to wait for winds, often marrying local women. This spread culture (e.g., Islam to East Africa and SE Asia). Arab/Persian merchants in East Africa, Chinese merchants in Southeast Asia, and Malay communities throughout the Indian Ocean.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ Effects: Rise of New States</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Trade wealth allowed for the formation of powerful kingdoms:
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border-l-4 border-yellow-400">
                <h4 className="font-bold text-yellow-900">🌍 Swahili City-States (East Africa)</h4>
                <p className="text-sm text-slate-700 mt-1">Examples: Kilwa, Mombasa. Traded gold, ivory, and slaves for porcelain and cotton. Became Islamic and mixed Bantu language with Arabic to create <strong>Swahili</strong>.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">🏝️ Sultanate of Malacca (Southeast Asia)</h4>
                <p className="text-sm text-slate-700 mt-1">Controlled the <strong>Strait of Malacca</strong> (chokepoint between Indian Ocean and South China Sea). Became wealthy by taxing passing ships.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🧵 Gujarat (India)</h4>
                <p className="text-sm text-slate-700 mt-1">Major manufacturing hub for textiles.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⛵ Ming Admiral Zheng He</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">📜 Context</h4>
                <p className="text-sm text-slate-700 mt-1">The Ming Dynasty overthrew the Mongols and wanted to display China's power.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🚢 The Voyages (1405–1433)</h4>
                <p className="text-sm text-slate-700 mt-1">Zheng He led massive treasure fleets (300+ ships) throughout the Indian Ocean to collect <strong>tribute</strong> and display the might of the Ming Emperor.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🔚 Outcome</h4>
                <p className="text-sm text-slate-700 mt-1">The voyages were stopped by Confucian scholar-bureaucrats who viewed them as too expensive and a threat to traditional Chinese culture. China retreated into isolationism.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-4",
      title: "Topic 2.4: Trans-Saharan Trade Routes",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏜️ Causes of Growth</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🐪 Technology</h4>
                <p className="text-sm text-slate-700 mt-1">The introduction of the <strong>camel</strong> (from Arabia) and the <strong>camel saddle</strong> allowed traders to traverse the Sahara Desert. Camels could go 10 days without water.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">💎 Goods</h4>
                <p className="text-sm text-slate-700 mt-1">The trade was driven by the exchange of <strong>Gold</strong> (from West Africa) for <strong>Salt</strong> (from North Africa/Sahara).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👑 Effects: Rise of West African Empires</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The wealth from taxing trade led to the rise of powerful empires:
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border-l-4 border-amber-400">
                <h4 className="font-bold text-amber-900">🦁 Mali Empire (c. 1230–1600)</h4>
                <div className="space-y-2 mt-2 text-sm text-slate-700">
                  <p><strong>Founded by Sundiata</strong> (the Lion Prince).</p>
                  <p><strong>Mansa Musa:</strong> Ruled Mali at its peak. His pilgrimage to Mecca (Hajj) in 1324 displayed Mali's gold wealth to the world, causing inflation in Egypt and attracting Islamic scholars to Mali.</p>
                  <p><strong>Timbuktu:</strong> Became a major center of Islamic learning and trade (books were a major luxury good here).</p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">☪️ Spread of Islam</h4>
                <p className="text-sm text-slate-700 mt-1">Merchants and rulers converted to Islam for trade connections and literacy, though common people often kept traditional animist beliefs (syncretism).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-5",
      title: "Topic 2.5: Cultural Consequences of Connectivity",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🙏 Diffusion of Religion</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">☸️ Buddhism</h4>
                <p className="text-sm text-slate-700 mt-1">Spread from India to China (via Silk Road) and to Southeast Asia. Evolved into <strong>Mahayana Buddhism</strong> (more accessible to commoners) and adopted Daoist terms (Zen Buddhism).</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🏛️ Hinduism/Buddhism in SE Asia</h4>
                <p className="text-sm text-slate-700 mt-1">The <strong>Khmer Empire</strong> (Angkor Wat) shows the shift from Hindu to Buddhist influence in architecture.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">☪️ Islam</h4>
                <p className="text-sm text-slate-700 mt-1">Spread to Sub-Saharan Africa via Trans-Saharan trade (Timbuktu), to India via conquest (Delhi Sultanate) and Sufi missionaries, and to Southeast Asia via Indian Ocean trade (Java, Malacca).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔬 Diffusion of Science and Technology</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">💥 Gunpowder/Paper</h4>
                <p className="text-sm text-slate-700 mt-1">Spread from China → Middle East → Europe (leading to the decline of knights/castles and rise of literacy).</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🔢 Mathematics</h4>
                <p className="text-sm text-slate-700 mt-1">"Arabic numerals" originated in India, traveled to the Islamic world, then to Europe.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🧭 Navigation</h4>
                <p className="text-sm text-slate-700 mt-1">Compass and Astrolabe technology spread west.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✈️ Notable Travelers</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">🧳 Ibn Battuta (Muslim)</h4>
                <p className="text-sm text-slate-700 mt-1">Traveled 75,000 miles throughout Dar al-Islam (Africa, Middle East, India, SE Asia). Wrote about the varying practices of Islam (e.g., shocked by the freedom of women in West Africa).</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🗺️ Marco Polo (Christian)</h4>
                <p className="text-sm text-slate-700 mt-1">Traveled from Venice to the court of Kublai Khan. His writings increased European interest in Asian goods.</p>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border-l-4 border-pink-400">
                <h4 className="font-bold text-pink-900">📖 Margery Kempe (Christian)</h4>
                <p className="text-sm text-slate-700 mt-1">Her autobiography offers insight into the life of a middle-class medieval woman and religious pilgrim.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-6",
      title: "Topic 2.6: Environmental Consequences of Connectivity",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌾 Diffusion of Crops</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Trade routes introduced crops to new regions, increasing populations.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🍚 Champa Rice</h4>
                <p className="text-sm text-slate-700 mt-1">Fast-ripening, drought-resistant rice from Vietnam sent to China. Allowed for two harvests per year → massive population boom in Song China.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🍌 Bananas</h4>
                <p className="text-sm text-slate-700 mt-1">Introduced to Sub-Saharan Africa by Indonesian seafarers. Being nutrient-rich, this led to population growth and migration (Bantu).</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🍊 Citrus/Sugar</h4>
                <p className="text-sm text-slate-700 mt-1">Spread by Caliphates into the Mediterranean.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🦠 Diffusion of Disease</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">💀 The Bubonic Plague (Black Death)</h4>
                <div className="space-y-2 mt-2 text-sm text-slate-700">
                  <p><strong>Origins:</strong> Spread from China via the Silk Roads and Mongol conquests.</p>
                  <p><strong>Impact on Europe (1347–1351):</strong> Killed 1/3 to 1/2 of the population.</p>
                  <p><strong>Social Consequence:</strong> Labor shortage led to higher wages for survivors and contributed to the decline of <strong>Feudalism</strong> (serfs could demand better pay or leave).</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-7",
      title: "Topic 2.7: Comparison of Economic Exchange",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Similarities Across Networks</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Silk Road, Trans-Saharan, and Indian Ocean trade networks shared common characteristics:
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🏛️ Origins</h4>
                <p className="text-sm text-slate-700 mt-1">All depended on the stability of large states (Mongols, Caliphates, Mali) to protect routes.</p>
              </div>
              <div className="bg-gradient-to-br from-green-100 to-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🎯 Purpose</h4>
                <p className="text-sm text-slate-700 mt-1">All facilitated the exchange of goods AND culture (religion, tech, disease).</p>
              </div>
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🌆 Growth</h4>
                <p className="text-sm text-slate-700 mt-1">All gave rise to new trading cities (Timbuktu, Swahili Coast, Kashgar, Hangzhou).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Differences Across Networks</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">💎 Goods</h4>
                <div className="space-y-1 mt-2 text-sm text-slate-700">
                  <p><strong>Silk Road:</strong> Luxury goods (silk, porcelain).</p>
                  <p><strong>Indian Ocean:</strong> Bulk goods (timber, grain, spices).</p>
                  <p><strong>Trans-Saharan:</strong> Gold, salt, slaves.</p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🚚 Transport</h4>
                <div className="space-y-1 mt-2 text-sm text-slate-700">
                  <p><strong>Silk/Sand:</strong> Horses and Camels (Caravans).</p>
                  <p><strong>Indian Ocean:</strong> Ships (Dhows, Junks).</p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🌍 Environment</h4>
                <div className="space-y-1 mt-2 text-sm text-slate-700">
                  <p><strong>Silk/Sand:</strong> Limited by water access.</p>
                  <p><strong>Indian Ocean:</strong> Dependent on Monsoon winds.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <ImportantNote>
              All three networks (Silk Road, Trans-Saharan, Indian Ocean) were interconnected and interdependent. The movement of goods, people, and ideas across these networks fundamentally transformed societies and laid the groundwork for the modern world.
            </ImportantNote>
          </section>
        </div>
      )
    }
  ]
};

export default worldHistoryUnit2Notes;